import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

function getFrankOsInitialCart() {
  if (typeof window.frankosGetInitialCart === 'function') {
    return window.frankosGetInitialCart();
  }

  window.frankosGetInitialCart = function () {
    if (window.frankosInitialCartPromise) {
      return window.frankosInitialCartPromise;
    }

    window.frankosInitialCartPromise = fetch('/cart.js', {
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Winkelmand kon niet worden geladen.');
        }

        return response.json();
      })
      .catch((error) => {
        window.frankosInitialCartPromise = null;
        throw error;
      });

    return window.frankosInitialCartPromise;
  };

  return window.frankosGetInitialCart();
}

class HeaderActions extends Component {
  requiredRefs = ['liveRegion'];

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);

    this.#syncFrankOsCartStatus(true);
    this.#startFrankOsRealtimeWatcher();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  #onCartUpdate = (event) => {
    const cartCount = event.detail?.resource?.item_count;

    if (cartCount !== undefined) {
      this.refs.liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
      this.#setFrankOsCartStatus(cartCount);
      return;
    }

    if (event.detail?.data?.source === 'product-form-component' && Number(event.detail?.data?.itemCount) > 0) {
      this.#setFrankOsCartStatus(1);
      return;
    }

    this.#syncFrankOsCartStatus();
  };

  #getVisibleCartBubbleCount() {
    const bubbleTexts = document.querySelectorAll(
      '#header-component .cart-bubble__text-count, #header-component .cart-bubble__text'
    );

    for (const el of bubbleTexts) {
      const text = (el.textContent || '').trim();
      const number = parseInt(text.replace(/\D/g, ''), 10);

      if (!Number.isNaN(number)) {
        return number;
      }
    }

    const cartBubble = document.querySelector('#header-component .cart-bubble');

    if (!cartBubble) {
      return 0;
    }

    const bubbleText = (cartBubble.textContent || '').trim();
    const bubbleNumber = parseInt(bubbleText.replace(/\D/g, ''), 10);

    if (!Number.isNaN(bubbleNumber)) {
      return bubbleNumber;
    }

    return 0;
  }

  #syncFrankOsCartStatus(useSharedInitialCart = false) {
    const domCount = this.#getVisibleCartBubbleCount();

    if (domCount === 0) {
      this.#setFrankOsCartStatus(0);
    }

    const cartRequest = useSharedInitialCart
      ? getFrankOsInitialCart()
      : fetch('/cart.js', {
          cache: 'no-store',
          headers: { Accept: 'application/json' },
        }).then((response) => {
          if (!response.ok) {
            throw new Error('Winkelmand kon niet worden geladen.');
          }

          return response.json();
        });

    cartRequest
      .then((cart) => {
        this.#setFrankOsCartStatus(cart.item_count || 0);
      })
      .catch(() => {
        this.#setFrankOsCartStatus(domCount);
      });
  }

  #setFrankOsCartStatus(count) {
    const filled = Number(count) > 0;

    document.querySelectorAll('#header-component cart-icon').forEach((cartIcon) => {
      cartIcon.classList.toggle('frankos-cart-filled', filled);
    });

    document.body.classList.toggle('frankos-cart-has-items', filled);
  }

  #startFrankOsRealtimeWatcher() {
    if (window.frankosCartRealtimeWatcherActive) return;
    window.frankosCartRealtimeWatcherActive = true;

    setInterval(() => {
      const domCount = this.#getVisibleCartBubbleCount();

      if (domCount === 0) {
        this.#setFrankOsCartStatus(0);
      } else {
        this.#setFrankOsCartStatus(domCount);
      }
    }, 250);
  }
}

if (!customElements.get('header-actions')) {
  customElements.define('header-actions', HeaderActions);
}

(() => {
  if (window.frankosV322) return;
  window.frankosV322 = true;

  let resizeTimer;

  const refreshHeader = () => {
    clearTimeout(resizeTimer);

    resizeTimer = setTimeout(() => requestAnimationFrame(() => {
      const header = document.querySelector('#header-component');
      const list = header?.querySelector('overflow-list');

      if (!header || !list) return;

      list.dispatchEvent(new CustomEvent('reflow', { detail: {} }));

      requestAnimationFrame(() => {
        const touch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

        header.dataset.menuStyle =
          touch || list.hasAttribute('minimum-reached')
            ? 'drawer'
            : 'menu';
      });
    }), 140);
  };

  addEventListener('resize', refreshHeader, { passive: true });
  visualViewport?.addEventListener('resize', refreshHeader, { passive: true });
  addEventListener('pageshow', refreshHeader);

  const route = (path) =>
    String(Shopify?.routes?.root || '/').replace(/\/?$/, '/') + path;

  const exactCart = () =>
    fetch(route('cart.js'), {
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    }).then((response) =>
      response.ok ? response.json() : Promise.reject()
    );

  const emitExact = (cart) =>
    document.dispatchEvent(
      new CustomEvent('cart:updated', {
        bubbles: true,
        detail: { resource: cart },
      })
    );

  const reconcile = () => {
    const run = () =>
      exactCart()
        .then(emitExact)
        .catch(() => {});

    if ('requestIdleCallback' in window) {
      requestIdleCallback(run, { timeout: 800 });
    } else {
      setTimeout(run, 250);
    }
  };

  const parts = (control) => {
    const home = control.hasAttribute('data-frankos-home-cart-controls');

    return {
      count: control.querySelector(
        home
          ? '.frankos-home__cart-count'
          : '.frankos-col__cart-count'
      ),
      minus: control.querySelector(
        home
          ? '[data-frankos-home-cart-action="decrease"]'
          : '[data-frankos-cart-action="decrease"]'
      ),
    };
  };

  const increment = (control) => {
    const p = parts(control);
    const n = Number(control.dataset.cartQuantity || 0) + 1;

    control.dataset.cartQuantity = String(n);
    control.classList.add('has-items');

    if (p.count) {
      p.count.textContent = String(n);
    }

    if (p.minus) {
      p.minus.disabled = false;
    }
  };

  const cartSectionIds = () =>
    [...new Set(
      [...document.querySelectorAll('cart-items-component[data-section-id]')]
        .map((component) => component.dataset.sectionId)
        .filter(Boolean)
    )].slice(0, 5);

  const getAddedCartImageUrl = (data) => {
    const sections = data?.sections;

    if (!sections || typeof sections !== 'object') {
      return '';
    }

    const addedKey = String(data?.items?.[0]?.key || '');

    for (const html of Object.values(sections)) {
      if (typeof html !== 'string' || !html) continue;

      const doc = new DOMParser().parseFromString(html, 'text/html');

      const rows = [
        ...doc.querySelectorAll(
          '.cart-items__table-row[data-key]'
        ),
      ];

      let row = addedKey
        ? rows.find(
            (candidate) =>
              String(candidate.dataset.key || '') === addedKey
          )
        : null;

      if (!row) {
        row = rows[0] || null;
      }

      const image = row?.querySelector(
        '.cart-items__media-image'
      );

      const src = image?.getAttribute('src');

      if (src) {
        try {
          return new URL(src, document.baseURI).href;
        } catch (_) {
          return src;
        }
      }
    }

    return '';
  };

  const preloadCartImage = (url, timeout = 1500) => {
    if (!url) {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      const image = new Image();

      let settled = false;

      const finish = () => {
        if (settled) return;

        settled = true;

        clearTimeout(timer);

        image.onload = null;
        image.onerror = null;

        resolve();
      };

      const timer = setTimeout(finish, timeout);

      image.onload = async () => {
        try {
          if (typeof image.decode === 'function') {
            await image.decode();
          }
        } catch (_) {
          // Decode-fout mag de winkelmand niet blokkeren.
        }

        finish();
      };

      image.onerror = finish;

      image.src = url;

      if (image.complete && image.naturalWidth > 0) {
        Promise.resolve(
          typeof image.decode === 'function'
            ? image.decode().catch(() => {})
            : undefined
        ).finally(finish);
      }
    });
  };

  const emitAdd = (id, sections) =>
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: undefined,
          sourceId: 'frankos-v322',
          data: {
            source: 'product-form-component',
            frankosFastAdd: true,
            itemCount: 1,
            variantId: String(id),
            sections: sections || {},
          },
        },
      })
    );

  const waitForFrankOsAddPaint = () =>
    new Promise((resolve) => {
      requestAnimationFrame(() => {
        requestAnimationFrame(resolve);
      });
    });

  const beginFrankOsOverviewAddFeedback = (control) => {
    if (!control || control.dataset.frankosOverviewPending === '1') {
      return () => {};
    }

    control.dataset.frankosOverviewPending = '1';
    control.setAttribute('aria-busy', 'true');

    const rect = control.getBoundingClientRect();
    const compact = window.matchMedia('(max-width: 420px)').matches;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const width = compact ? 146 : 164;
    const height = Math.max(38, Math.round(rect.height || 38));
    const viewportWidth = Math.max(document.documentElement.clientWidth || 0, innerWidth || 0);
    const viewportHeight = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0);
    const left = Math.max(8, Math.min(Math.round(rect.right - width), viewportWidth - width - 8));
    const top = Math.max(8, Math.min(Math.round(rect.top + ((rect.height || height) - height) / 2), viewportHeight - height - 8));

    const feedback = document.createElement('span');
    const spinner = document.createElement('span');
    const text = document.createElement('span');

    feedback.setAttribute('aria-hidden', 'true');
    feedback.dataset.frankosOverviewAddFeedback = '1';
    text.textContent = 'Wordt toegevoegd…';

    Object.assign(feedback.style, {
      position: 'fixed',
      left: `${left}px`,
      top: `${top}px`,
      zIndex: '2147483000',
      boxSizing: 'border-box',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: compact ? '6px' : '8px',
      width: `${width}px`,
      height: `${height}px`,
      padding: compact ? '0 9px' : '0 12px',
      border: '1px solid rgba(118, 206, 255, 0.92)',
      borderRadius: '999px',
      background: '#0033ff',
      color: '#ffffff',
      fontFamily: 'var(--font-body--family)',
      fontSize: compact ? '11px' : '12px',
      fontWeight: '800',
      lineHeight: '1',
      whiteSpace: 'nowrap',
      opacity: '1',
      visibility: 'visible',
      pointerEvents: 'none',
      boxShadow: '0 0 16px rgba(0, 51, 255, 0.58), inset 0 1px 0 rgba(255, 255, 255, 0.18)',
    });

    Object.assign(spinner.style, {
      boxSizing: 'border-box',
      display: 'block',
      width: '18px',
      height: '18px',
      minWidth: '18px',
      minHeight: '18px',
      border: '2px solid currentColor',
      borderRightColor: reduceMotion ? 'currentColor' : 'transparent',
      borderRadius: '50%',
      flex: '0 0 auto',
    });

    feedback.append(spinner, text);
    document.body.appendChild(feedback);

    const previousOpacity = control.style.opacity;
    const previousVisibility = control.style.visibility;
    const previousPointerEvents = control.style.pointerEvents;

    control.style.setProperty('opacity', '0', 'important');
    control.style.setProperty('visibility', 'hidden', 'important');
    control.style.setProperty('pointer-events', 'none', 'important');

    let spinnerAnimation = null;
    if (!reduceMotion && typeof spinner.animate === 'function') {
      spinnerAnimation = spinner.animate(
        [{ transform: 'rotate(0deg)' }, { transform: 'rotate(360deg)' }],
        { duration: 550, iterations: Infinity, easing: 'linear' }
      );
    }

    const pendingCount = Number(window.frankosCartPendingCount || 0) + 1;
    window.frankosCartPendingCount = pendingCount;
    document.documentElement.classList.add('frankos-cart-add-pending');

    let ended = false;
    return () => {
      if (ended) return;
      ended = true;

      try { spinnerAnimation?.cancel(); } catch (_) {}
      feedback.remove();

      if (previousOpacity) control.style.opacity = previousOpacity;
      else control.style.removeProperty('opacity');

      if (previousVisibility) control.style.visibility = previousVisibility;
      else control.style.removeProperty('visibility');

      if (previousPointerEvents) control.style.pointerEvents = previousPointerEvents;
      else control.style.removeProperty('pointer-events');

      control.removeAttribute('aria-busy');
      delete control.dataset.frankosOverviewPending;

      const nextPendingCount = Math.max(0, Number(window.frankosCartPendingCount || 1) - 1);
      window.frankosCartPendingCount = nextPendingCount;
      if (nextPendingCount === 0) {
        document.documentElement.classList.remove('frankos-cart-add-pending');
      }
    };
  };

  document.addEventListener(
    'click',
    async (event) => {
      const button = event.target.closest?.(
        '[data-frankos-home-cart-action="increase"], [data-frankos-cart-action="increase"]'
      );

      const control = button?.closest?.(
        '[data-frankos-home-cart-controls], [data-frankos-cart-controls]'
      );

      if (
        !button ||
        !control ||
        control.dataset.directCart !== 'true'
      ) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      if (control.dataset.frankosV322Busy === '1') {
        return;
      }

      const id = Number(control.dataset.variantId);

      if (!Number.isFinite(id) || id <= 0) {
        return;
      }

      control.dataset.frankosV322Busy = '1';
      const endFeedback = beginFrankOsOverviewAddFeedback(control);

      try {
        // Geef de browser eerst twee paint-frames, exact vóór de Shopify-request.
        // Daardoor ziet de klant direct dat de klik is ontvangen.
        await waitForFrankOsAddPaint();

        const sectionIds = cartSectionIds();

        const response = await fetch(route('cart/add.js'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            items: [
              {
                id,
                quantity: 1,
              },
            ],
            ...(sectionIds.length
              ? {
                  sections: sectionIds.join(','),
                }
              : {}),
          }),
        });

        const data = await response.json();

        if (!response.ok || data.status) {
          if (
            response.status === 422 ||
            Number(data.status) === 422
          ) {
            control.classList.add(
              'stock-limit-reached'
            );

            setTimeout(() => {
              control.classList.remove(
                'stock-limit-reached'
              );
            }, 2200);
          }

          throw new Error(
            data.description ||
              data.message ||
              'Toevoegen is niet gelukt.'
          );
        }

        increment(control);

        // Zelfde kritieke volgorde als op de productpagina: zodra Shopify de add
        // bevestigt, de drawer direct bijwerken/openen. Afbeeldings-preload mag
        // de zichtbare cartreactie niet langer blokkeren.
        emitAdd(id, data.sections);

        const cartImageUrl = getAddedCartImageUrl(data);
        void preloadCartImage(cartImageUrl);

        reconcile();
      } catch (_) {
        exactCart()
          .then(emitExact)
          .catch(() => {});
      } finally {
        endFeedback();
        delete control.dataset.frankosV322Busy;
      }
    },
    true
  );
})();